/**
 * IMBEE-8069 (T86) — the welcome banner (Area A) and bottom quick-button bar (Area C)
 * portions of the widget settings returned by GET /widget.
 *
 * These mirror the service contract exactly (web-chat-widget.interface.ts). Every field is
 * optional: a channel configured before this feature shipped, or one with the feature off,
 * simply omits them, and the widget must render exactly as it does today.
 */

export enum WelcomeBannerImageMode {
    Default = 'default',
    Client = 'client',
}

export type WelcomeBannerBadge = {
    /** 6-digit hex, no leading '#'. */
    color: string;
    text: string;
};

export type WelcomeBannerButton = {
    label: string;
    badge?: WelcomeBannerBadge;
};

export type BottomButton = {
    label: string;
};

/**
 * The Area A + Area C slice of the widget settings, travelling as one prop from App down to
 * Conversation rather than eight separate ones through three components.
 */
export type QuickReplyConfig = {
    enableWelcomeBanner?: boolean;
    welcomeBannerImageMode?: WelcomeBannerImageMode;
    welcomeBannerImageUrl?: string | null;
    welcomeBannerButtons?: WelcomeBannerButton[];
    enableBottomButtons?: boolean;
    bottomButtons?: BottomButton[];
};

/** AC-A.2 — the banner shows this many buttons at a time, then pages. */
export const WELCOME_BANNER_BUTTONS_PER_PAGE = 4;

/**
 * AC-A.2 — the banner's buttons split into pages. Order is preserved; the last page may be
 * short. An empty list yields no pages at all, so the banner can hide its pager entirely
 * rather than render "page 1 of 0".
 */
export const paginateBannerButtons = (
    buttons: WelcomeBannerButton[] = [],
    perPage: number = WELCOME_BANNER_BUTTONS_PER_PAGE,
): WelcomeBannerButton[][] => {
    const size = Math.max(1, perPage);
    const pages: WelcomeBannerButton[][] = [];
    for (let i = 0; i < buttons.length; i += size) {
        pages.push(buttons.slice(i, i + size));
    }
    return pages;
};

/**
 * AC-A.3 — a badge renders only when it carries BOTH a colour and a text. Half a badge is an
 * empty coloured pill, which reads as a rendering bug rather than as "no badge".
 */
export const hasRenderableBadge = (button: WelcomeBannerButton): boolean =>
    Boolean(button.badge && button.badge.color && button.badge.text);


/**
 * IMBEE-9231 (T86 AC-C.1) — read the visitor's bottom buttons off whatever the network gave us.
 *
 * The widget no longer decides which buttons a visitor may see. It used to hold the ticket's
 * tags and run the displayCondition predicate itself, which meant internal CRM state — VIP,
 * chargeback risk — had to be shipped to the browser. AC-1 decided none of it is publishable,
 * so web-chat-services evaluates the conditions and sends the surviving labels; this just
 * renders them.
 *
 * Two untrusted sources feed it: `bottom_buttons` on the session create/restore response, and
 * the body of a `{sessionId}-bottom-buttons` stomp frame, which is a JSON.parse of a string.
 *
 * Returns null for anything unusable, and callers IGNORE a null rather than applying it. That
 * matters: coercing to [] would blank a working bar because one malformed frame arrived,
 * whereas keeping the previous list is both safer and self-correcting — the next push or the
 * next session restore replaces it.
 */
export const normalizeBottomButtons = (value: unknown): BottomButton[] | null =>
    Array.isArray(value)
        ? value
              .filter(
                  (button): button is BottomButton =>
                      Boolean(button) &&
                      typeof (button as BottomButton).label === 'string' &&
                      (button as BottomButton).label.length > 0,
              )
              .map(button => ({ label: button.label }))
        : null;
